/**
 * DateRange Field Component
 * Wrapper for DateRangePicker with react-hook-form integration
 */
import React, { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import { DateCalendar } from '@mui/x-date-pickers'
import {
  TextField,
  Box,
  Popover,
  IconButton,
  Chip,
  Stack,
  FormControl,
  InputLabel
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'
import { Controller, useWatch } from 'react-hook-form'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'

interface DateRangeValue {
  start: string | null
  end: string | null
}

const DateRangeField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    required,
    disabled,
    helperText,
    fieldProps = {}
  } = config

  // Extract specific props from fieldProps
  const { presetRanges = [], sx: fieldSx, InputLabelProps } = fieldProps

  // Get form context for change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // Component state
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [leftPanelMonth, setLeftPanelMonth] = useState(
    dayjs().subtract(1, 'month')
  )
  const [rightPanelMonth, setRightPanelMonth] = useState(dayjs())

  // Check if the field is changed for highlighting
  const isFieldChanged = useCallback(() => {
    if (config.enableChangeHighlight !== true) {
      return false
    }

    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // Compare the entire date range objects
      const origStr = JSON.stringify(originalValue || {})
      const currStr = JSON.stringify(currentValue || {})

      return origStr !== currStr
    }
    return false
  }, [config.enableChangeHighlight, formContext, formValues, name])

  // Check if the field is changed
  const isChanged = isFieldChanged()

  // Check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // Merge styles
  const mergedSx = {
    '& .MuiInputBase-root': {
      height: '36px'
    },
    ...(isChanged && {
      '& .MuiInputBase-input': {
        color: 'secondary.dark',
        fontWeight: 'bold'
      }
    }),
    ...(fieldSx || {})
  }

  // Handle opening the date picker popover
  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    // Reset panel months when opening
    setLeftPanelMonth(dayjs().subtract(1, 'month'))
    setRightPanelMonth(dayjs())
    setAnchorEl(event.currentTarget)
  }, [])

  // Handle closing the date picker popover
  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  // Handle date selection in the calendar
  const handleDateSelect = useCallback(
    (date: dayjs.Dayjs | null, onChange: (value: DateRangeValue) => void) => {
      if (!date) return

      if (!startDate && !endDate) {
        const newStartDate = date
        setStartDate(newStartDate)
        onChange({
          start: newStartDate.format('YYYY-MM-DD'),
          end: null
        })
      } else if (startDate && !endDate) {
        if (date.isBefore(startDate)) {
          const newEndDate = startDate
          const newStartDate = date
          setEndDate(newEndDate)
          setStartDate(newStartDate)
          onChange({
            start: newStartDate.format('YYYY-MM-DD'),
            end: newEndDate.format('YYYY-MM-DD')
          })
        } else {
          const newEndDate = date
          setEndDate(newEndDate)
          onChange({
            start: startDate.format('YYYY-MM-DD'),
            end: newEndDate.format('YYYY-MM-DD')
          })
        }
      } else if (startDate && endDate) {
        // Reset both dates and set the new date as startDate
        const newStartDate = date
        setStartDate(newStartDate)
        setEndDate(null)
        onChange({
          start: newStartDate.format('YYYY-MM-DD'),
          end: null
        })
      }
    },
    [startDate, endDate]
  )

  // Handle left calendar month change
  const handleLeftMonthChange = useCallback(
    (month: dayjs.Dayjs) => {
      setLeftPanelMonth(month)
      // If left panel would show same month as right panel, move right panel forward
      if (month.isSame(rightPanelMonth, 'month')) {
        setRightPanelMonth(month.add(1, 'month'))
      }
      // If left panel would be after right panel, move right panel forward
      else if (month.isAfter(rightPanelMonth)) {
        setRightPanelMonth(month.add(1, 'month'))
      }
    },
    [rightPanelMonth]
  )

  // Handle right calendar month change
  const handleRightMonthChange = useCallback(
    (month: dayjs.Dayjs) => {
      setRightPanelMonth(month)
      // If right panel would show same month as left panel, move left panel backward
      if (month.isSame(leftPanelMonth, 'month')) {
        setLeftPanelMonth(month.subtract(1, 'month'))
      }
      // If right panel would be before left panel, move left panel backward
      else if (month.isBefore(leftPanelMonth)) {
        setLeftPanelMonth(month.subtract(1, 'month'))
      }
    },
    [leftPanelMonth]
  )

  // Handle preset range selection
  const handlePresetRange = useCallback(
    (days: number, onChange: (value: DateRangeValue) => void) => {
      const end = dayjs()
      const start = end.subtract(days, 'day')
      setStartDate(start)
      setEndDate(end)
      onChange({
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD')
      })
    },
    []
  )

  // Handle clearing selected dates
  const handleClearSelectDate = useCallback(
    (
      event: React.MouseEvent | undefined,
      onChange: (value: DateRangeValue) => void
    ) => {
      if (event) event.stopPropagation()
      setStartDate(null)
      setEndDate(null)
      onChange({
        start: null,
        end: null
      })
      // Reset calendar panels
      setLeftPanelMonth(dayjs().subtract(1, 'month'))
      setRightPanelMonth(dayjs())
    },
    []
  )

  // Check if a date is in the selected range
  const isInRange = useCallback(
    (date: dayjs.Dayjs) => {
      if (!startDate || !endDate) return false
      return date.isAfter(startDate, 'day') && date.isBefore(endDate, 'day')
    },
    [startDate, endDate]
  )

  // Check if a day is selected (start or end date)
  const isDaySelected = useCallback(
    (day: dayjs.Dayjs) => {
      if (day.isSame(startDate, 'day')) return true
      if (!endDate) return false
      return day.isSame(endDate, 'day')
    },
    [startDate, endDate]
  )

  // Watch field value at component top level (not inside render)
  // This ensures we follow React Hooks rules
  const fieldValue = useWatch({
    control,
    name
  })

  // Sync local state with field value
  // This effect runs at component top level, following Hooks rules
  useEffect(() => {
    if (fieldValue) {
      if (fieldValue.start) {
        setStartDate(dayjs(fieldValue.start))
      } else {
        setStartDate(null)
      }
      if (fieldValue.end) {
        setEndDate(dayjs(fieldValue.end))
      } else {
        setEndDate(null)
      }
    } else {
      // Clear local state when field value is null/undefined
      // This ensures UI matches form state after linkage rules clear the field
      setStartDate(null)
      setEndDate(null)
    }
  }, [fieldValue])

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: required
          ? (value: DateRangeValue | undefined) => {
              // Check if value is undefined or null
              if (!value) {
                return `${label} is required`
              }
              // Check if both start and end are null or empty
              if (
                (!value.start || value.start === null || value.start === '') &&
                (!value.end || value.end === null || value.end === '')
              ) {
                return `${label} is required`
              }
              // Check if start is missing
              if (!value.start || value.start === null || value.start === '') {
                return `Start date is required`
              }
              // Check if end is missing
              if (!value.end || value.end === null || value.end === '') {
                return `End date is required`
              }
              return true
            }
          : undefined
      }}
      render={({ field }) => {
        // Handle value change - component manages its own format
        const handleChange = (value: DateRangeValue) => {
          // Update form field value with component's format
          field.onChange(value)
        }

        // Format display values
        const startDisplayValue = startDate
          ? startDate.format('YYYY/MM/DD')
          : ''
        const endDisplayValue = endDate ? endDate.format('YYYY/MM/DD') : ''

        const open = Boolean(anchorEl)

        return (
          <FormControl
            variant='outlined'
            fullWidth
            required={required}
            disabled={disabled || isReadOnly}
            error={!!errors[name]}
            sx={{
              '& .MuiFormLabel-root': {
                backgroundColor: 'white',
                px: 1,
                '&.Mui-focused': {
                  color: 'primary.main'
                }
              },
              ...mergedSx
            }}>
            <InputLabel shrink={InputLabelProps?.shrink !== false}>
              {label}
            </InputLabel>
            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)'
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                minHeight: '36px',
                height: '36px',
                position: 'relative',
                ...(errors[name] ? { borderColor: 'error.main' } : {})
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flex: 1,
                  // Reserve space for clear button when it's visible
                  marginRight:
                    (startDate || endDate) && !disabled && !isReadOnly
                      ? '28px'
                      : 0,
                  minWidth: 0, // Allow flex item to shrink below content size
                  overflow: 'hidden' // Prevent content from overflowing
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0, // Allow flex item to shrink below content size
                    overflow: 'hidden' // Prevent content from overflowing
                  }}>
                  <TextField
                    value={startDisplayValue}
                    variant='standard'
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        // Apply text overflow handling
                        '& input': {
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }
                      }
                    }}
                    sx={{
                      width: '100px',
                      minWidth: 0, // Allow shrinking
                      flexShrink: 1 // Allow this field to shrink
                    }}
                    disabled={disabled || isReadOnly}
                    placeholder='YYYY/MM/DD'
                    onClick={!disabled && !isReadOnly ? handleOpen : undefined}
                  />
                  <span
                    style={{
                      color: '#999999',
                      fontSize: '8px',
                      flexShrink: 0 // Don't shrink the separator
                    }}>
                    —
                  </span>
                  <TextField
                    value={endDisplayValue}
                    variant='standard'
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        // Apply text overflow handling
                        '& input': {
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }
                      }
                    }}
                    sx={{
                      width: '100px',
                      minWidth: 0, // Allow shrinking
                      flexShrink: 1 // Allow this field to shrink
                    }}
                    disabled={disabled || isReadOnly}
                    placeholder='YYYY/MM/DD'
                    onClick={!disabled && !isReadOnly ? handleOpen : undefined}
                  />
                </Box>
              </Box>
              {(startDate || endDate) && !disabled && !isReadOnly && (
                <IconButton
                  size='small'
                  sx={{
                    position: 'absolute',
                    right: '50px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    padding: 0.5,
                    ':hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '.MuiBox-root:hover &': {
                      opacity: 1
                    }
                  }}
                  onClick={e => handleClearSelectDate(e, handleChange)}>
                  <ClearIcon fontSize='small' />
                </IconButton>
              )}

              <IconButton
                onClick={handleOpen}
                edge='end'
                sx={{ mr: -0.5 }}
                disabled={disabled || isReadOnly}>
                <CalendarTodayIcon />
              </IconButton>
            </Box>

            {(errors[name] || helperText) && (
              <Box
                sx={{
                  color: errors[name] ? 'error.main' : 'text.secondary',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  ml: 1.5
                }}>
                {errors[name]?.message || helperText}
              </Box>
            )}

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}>
              <Box>
                <Box sx={{ display: 'flex' }}>
                  <DateCalendar
                    key={`left-${leftPanelMonth.format('YYYY-MM')}-${startDate?.format('YYYY-MM-DD') || ''}-${endDate?.format('YYYY-MM-DD') || ''}`}
                    referenceDate={leftPanelMonth}
                    onMonthChange={handleLeftMonthChange}
                    onChange={date => handleDateSelect(date, handleChange)}
                    sx={{
                      '& .MuiPickersYear-yearButton': {
                        fontSize: '14px'
                      }
                    }}
                    slotProps={{
                      day: ({ day }) => ({
                        selected: isDaySelected(day),
                        sx: {
                          ...(day.isSame(startDate, 'day') && {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark'
                            }
                          }),
                          ...(endDate &&
                            day.isSame(endDate, 'day') && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark'
                              }
                            }),
                          ...(isInRange(day) && {
                            backgroundColor: 'primary.light',
                            opacity: 0.6,
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              opacity: 0.8
                            }
                          })
                        }
                      })
                    }}
                  />
                  <DateCalendar
                    key={`right-${rightPanelMonth.format('YYYY-MM')}-${startDate?.format('YYYY-MM-DD') || ''}-${endDate?.format('YYYY-MM-DD') || ''}`}
                    referenceDate={rightPanelMonth}
                    onMonthChange={handleRightMonthChange}
                    onChange={date => handleDateSelect(date, handleChange)}
                    sx={{
                      '& .MuiPickersYear-yearButton': {
                        fontSize: '14px'
                      }
                    }}
                    slotProps={{
                      day: ({ day }) => ({
                        selected: isDaySelected(day),
                        sx: {
                          ...(day.isSame(startDate, 'day') && {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark'
                            }
                          }),
                          ...(endDate &&
                            day.isSame(endDate, 'day') && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark'
                              }
                            }),
                          ...(isInRange(day) && {
                            backgroundColor: 'primary.light',
                            opacity: 0.6,
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              opacity: 0.8
                            }
                          })
                        }
                      })
                    }}
                  />
                </Box>
                {presetRanges && presetRanges.length > 0 && (
                  <Stack direction='row' spacing={1} sx={{ mb: 2, px: 2 }}>
                    {presetRanges.map(
                      (range: { label: string; days: number }) => (
                        <Chip
                          key={range.days}
                          label={range.label}
                          onClick={() =>
                            handlePresetRange(range.days, handleChange)
                          }
                          sx={{
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        />
                      )
                    )}
                  </Stack>
                )}
              </Box>
            </Popover>
          </FormControl>
        )
      }}
    />
  )
}

export default DateRangeField
